
import React from 'react';
import { Link } from 'react-router-dom';

interface ButtonProps {
  children?: React.ReactNode;
  href?: string;
  to?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'outline-light' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  href,
  to,
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  className = '',
  type,
  disabled,
  loading = false,
  onClick,
  ...props
}) => {
  const baseStyles =
    'inline-flex items-center justify-center font-medium uppercase tracking-eyebrow-sm ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'transition-colors duration-300 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';

  const variantStyles = {
    // Orange fill — brand signature CTA
    primary:
      'bg-brand-accent text-white ' +
      'hover:bg-brand-accent-dark focus-visible:ring-brand-accent/40',

    // Dark fill — prominent non-orange actions
    secondary:
      'bg-light-text-primary text-white ' +
      'hover:bg-[#595959] focus-visible:ring-light-text-primary/30',

    // Bordered transparent — secondary actions on light surfaces
    outline:
      'bg-transparent text-light-text-primary border border-light-text-primary ' +
      'hover:bg-light-text-primary hover:text-white ' +
      'focus-visible:ring-light-text-primary/30',

    // Bordered transparent — secondary actions on dark surfaces (hero, CTA bands)
    'outline-light':
      'bg-transparent text-white border border-white/20 ' +
      'hover:bg-white/10 focus-visible:ring-white/30',

    // Subtle — tertiary / card-level actions
    ghost:
      'bg-transparent text-light-text-secondary border border-light-border ' +
      'hover:bg-light-elevated hover:text-light-text-primary focus-visible:ring-brand-accent/40',
  };

  const sizeStyles = {
    sm: 'px-4 py-2 text-[11px]',
    md: 'px-6 py-3 text-xs',
    lg: 'px-8 py-3.5 text-xs',
  };

  const iconSizeStyles = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const getIconMargin = () => {
    if (!children) return '';
    return iconPosition === 'left' ? 'mr-2' : 'ml-2';
  };

  const iconSizeClass = iconSizeStyles[size];

  const finalClassName = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`.trim();

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg 
      className={`animate-spin ${iconSizeClass} ${children ? 'mr-2' : ''}`}
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle 
        className="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        strokeWidth="4"
      />
      <path 
        className="opacity-75" 
        fill="currentColor" 
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const content = (
    <>
      {loading && <LoadingSpinner />}
      {!loading && icon && iconPosition === 'left' && (
        <span className={`${getIconMargin()} ${iconSizeClass} flex items-center justify-center`}>
          {icon}
        </span>
      )}
      {children && <span className="relative">{children}</span>}
      {!loading && icon && iconPosition === 'right' && (
        <span className={`${getIconMargin()} ${iconSizeClass} flex items-center justify-center`}>
          {icon}
        </span>
      )}
    </>
  );

  const isDisabled = disabled || loading;

  // Internal link (React Router)
  if (to && !isDisabled) {
    const handleClick = () => {
      if (onClick) onClick();
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      }, 100);
    };

    return (
      <Link to={to} className={finalClassName} onClick={handleClick}>
        {content}
      </Link>
    );
  }

  // External link or mailto
  if (href && !isDisabled) {
    return (
      <a href={href} className={finalClassName} onClick={onClick} {...props}>
        {content}
      </a>
    );
  }

  // Button element
  return (
    <button 
      type={type || 'button'} 
      className={finalClassName} 
      disabled={isDisabled}
      onClick={onClick}
      aria-busy={loading}
      {...props}
    >
      {content}
    </button>
  );
};
